import {
    ChangeEvent,
    useEffect,
    useState,
    KeyboardEvent,
    useContext,
} from "react";

import {
    Spinner,
    Toast,
    ToastContainer,
} from "@salesforce/design-system-react";

import {
    Input,
    Password,
    Select,
    TextArea,
    Toggle,
} from "@vimeo/iris/components";
import { Option } from "@vimeo/iris/components/inputs/Select/Select.minors";

import { Privacy, Video } from "vimeo";
import { client as vimeoClient } from "../vimeoClient";

import debounce from "lodash.debounce";
import classNames from "classnames";

import VideoSettingsContext, {
    VideoSettingsState,
} from "../contexts/videoSettingsContext";

import { AllowedDomains } from "./AllowedDomains";
import { CopyVideoLink } from "./CopyVideoLink";

import { VerticalDivider } from "../components/VerticalDivider";
import { VimeoPlayer } from "./VimeoPlayer";

import { isContentBlockContext } from "../sessionStorage";

import "./settings.scoped.scss";
import {
    EventName,
    getSfmcLocation,
    sendAnalyticsEvent,
} from "../analytics/common";
import { getVideoIdFromUri } from "../common";

export function BasicSettings() {
    const [videoSettingsContext, setVideoSettingsContext] =
        useContext(VideoSettingsContext);

    const [video, setVideo] = useState<Video>();
    const [nameInvalid, setNameInvalid] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [showVideoPasswordInput, setShowVideoPasswordInput] = useState(false);

    useEffect(() => {
        setVideo(videoSettingsContext.video);
    }, [videoSettingsContext]);

    useEffect(() => {
        if (!video) {
            return;
        }

        setShowVideoPasswordInput(!!video.password);

        sendAnalyticsEvent(EventName.pageView, {
            name: "video_settings",
            location: getSfmcLocation(),
            video_id: getVideoIdFromUri(video.uri),
            section: "basic",
        });
    }, [video]);

    function onKeyupName(e: KeyboardEvent) {
        const value = (e.target as HTMLInputElement).value;
        if (!value) {
            setNameInvalid(true);
            return;
        }
        setNameInvalid(false);
    }

    async function onNameChange(e: ChangeEvent) {
        const value = (e.target as HTMLInputElement).value;
        if (!value) {
            return;
        }

        const videoUri = videoSettingsContext.video?.uri.replace("/", "");

        if (!videoUri) {
            return;
        }

        try {
            const resp = await vimeoClient.patch<Video>(
                `/api/vimeo/${videoUri}`,
                {
                    name: value,
                }
            );
            setVideoSettingsContext({
                ...videoSettingsContext,
                video: resp.data,
            });
        } catch (err) {
            setError("Failed to save changes. Please try again later.");
        }
    }

    async function onDescriptionChange(e: ChangeEvent) {
        const videoUri = (
            videoSettingsContext as VideoSettingsState
        ).video?.uri.replace("/", "");

        if (!videoUri) {
            return;
        }

        try {
            const resp = await vimeoClient.patch<Video>(
                `/api/vimeo/${videoUri}`,
                {
                    description: (e.target as HTMLTextAreaElement).value,
                }
            );
            setVideoSettingsContext({
                ...videoSettingsContext,
                video: resp.data,
            });
        } catch (err) {
            setError("Failed to save changes. Please try again later.");
        }
    }

    async function onUpdatePrivacy(e: ChangeEvent, field: keyof Privacy) {
        const videoUri = (
            videoSettingsContext as VideoSettingsState
        ).video?.uri.replace("/", "");

        if (!videoUri) {
            return;
        }

        const value = (e.target as HTMLSelectElement).value;

        if (field === "view" && value === "password") {
            setShowVideoPasswordInput(true);
            return;
        }

        setLoading(true);
        try {
            const update: any = {};
            update[field] = value;
            const resp = await vimeoClient.patch<Video>(
                `/api/vimeo/${videoUri}`,
                {
                    privacy: update,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );
            setVideoSettingsContext({
                ...videoSettingsContext,
                video: resp.data,
            });
        } catch (err) {
            setError("Failed to save changes. Please try again later.");
        }

        setLoading(false);
    }

    async function onPasswordChange(e: ChangeEvent) {
        const videoUri = (
            videoSettingsContext as VideoSettingsState
        ).video?.uri.replace("/", "");

        if (!videoUri) {
            return;
        }

        const value = (e.target as HTMLSelectElement).value;
        if (!value) {
            return;
        }

        setLoading(true);
        try {
            const resp = await vimeoClient.patch<Video>(
                `/api/vimeo/${videoUri}`,
                {
                    password: value,
                    privacy: {
                        view: "password",
                    },
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );
            setVideoSettingsContext({
                ...videoSettingsContext,
                video: resp.data,
            });
        } catch (err) {
            setError("Failed to save changes. Please try again later.");
        }

        setLoading(false);
    }

    async function onDownloadStatusChange(e: ChangeEvent) {
        const videoUri = (
            videoSettingsContext as VideoSettingsState
        ).video?.uri.replace("/", "");

        if (!videoUri) {
            return;
        }

        setLoading(true);

        try {
            const resp = await vimeoClient.patch<Video>(
                `/api/vimeo/${videoUri}`,
                {
                    privacy: {
                        download: (e.target as HTMLInputElement).checked,
                    },
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );
            setVideoSettingsContext({
                ...videoSettingsContext,
                video: resp.data,
            });
        } catch (err) {
            setError("Failed to save changes. Please try again later.");
        }

        setLoading(false);
    }

    return (
        <>
            {loading && <Spinner />}
            {video && (
                <div className="slds-grid" style={{ height: "100%" }}>
                    <div className="slds-col">
                        {!videoSettingsContext.hideSettingsViewHeading && (
                            <div className="slds-text-heading_medium slds-var-p-bottom_large">
                                Basic
                            </div>
                        )}
                        <div
                            className={classNames([
                                "slds-form-element",
                                "slds-var-m-bottom_large",
                                { "slds-has-error": nameInvalid },
                            ])}
                        >
                            <div className="slds-form-element__control">
                                <Input
                                    defaultValue={video.name}
                                    onChange={debounce(onNameChange, 250)}
                                    onKeyUp={onKeyupName}
                                    aria-invalid={nameInvalid}
                                    aria-required="true"
                                    label={
                                        <label>
                                            Title
                                            <abbr
                                                className="slds-required"
                                                title="required"
                                            >
                                                *
                                            </abbr>
                                        </label>
                                    }
                                />
                            </div>
                            {nameInvalid && (
                                <div className="slds-form-element__help">
                                    Name is required
                                </div>
                            )}
                        </div>
                        <div className="slds-form-element slds-var-m-bottom_large">
                            <div className="slds-form-element__control">
                                <TextArea
                                    defaultValue={video.description}
                                    onChange={debounce(
                                        onDescriptionChange,
                                        250
                                    )}
                                    label="Description"
                                ></TextArea>
                            </div>
                        </div>
                        <div className="slds-form-element slds-var-m-bottom_large">
                            <div className="slds-form-element__control">
                                {/* The password privacy option will only save if the
                                password is also set. So until the user sets a password,
                                temporarily set the privacy option to password based on
                                the password input being visible. */}
                                <Select
                                    id="privacy"
                                    value={
                                        showVideoPasswordInput
                                            ? "password"
                                            : video.privacy.view
                                    }
                                    onChange={(e) => onUpdatePrivacy(e, "view")}
                                    label="Who can watch?"
                                >
                                    <Option value="unlisted">Unlisted</Option>
                                    <Option value="password">Password</Option>
                                    <Option value="disable">
                                        Hide from Vimeo
                                    </Option>
                                    <Option value="nobody">Private</Option>
                                    <Option value="anybody">Public</Option>
                                </Select>
                                {showVideoPasswordInput && (
                                    <div className="slds-var-m-top_small">
                                        <Password
                                            value={video.password}
                                            onChange={debounce(
                                                onPasswordChange,
                                                250
                                            )}
                                            size="md"
                                        />
                                    </div>
                                )}
                                <div className="slds-var-m-top_small">
                                    <CopyVideoLink video={video} />
                                </div>
                            </div>
                        </div>
                        <div className="slds-form-element slds-var-m-bottom_large">
                            <div className="slds-form-element__control">
                                <Select
                                    id="embed-location"
                                    value={video.privacy.embed}
                                    onChange={(e) =>
                                        onUpdatePrivacy(e, "embed")
                                    }
                                    label="Where can this be embedded?"
                                >
                                    <Option value="private">Private</Option>
                                    <Option value="public">Anywhere</Option>
                                    <Option value="whitelist">
                                        Specific domains
                                    </Option>
                                </Select>
                                {video.privacy.embed === "whitelist" && (
                                    <div
                                        key="allowed-domains"
                                        className="slds-var-m-top_small"
                                    >
                                        <AllowedDomains video={video} />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="slds-form-element slds-var-m-bottom_large">
                            <div className="slds-form-element__control">
                                <Select
                                    id="embed-location"
                                    value={video.privacy.comments}
                                    onChange={(e) =>
                                        onUpdatePrivacy(e, "comments")
                                    }
                                    label="Who can comment?"
                                >
                                    <Option value="anybody">Anyone</Option>
                                    <Option value="nobody">No one</Option>
                                    <Option value="contacts">
                                        People I follow
                                    </Option>
                                </Select>
                            </div>
                        </div>
                        <div className="slds-form-element">
                            <div className="slds-form-element__control">
                                <Toggle
                                    label="People can download this video"
                                    checked={video.privacy.download || false}
                                    onChange={onDownloadStatusChange}
                                />
                            </div>
                            <p className="slds-text-color_weak">
                                This applies to the review, video, and download
                                pages.
                            </p>
                        </div>
                    </div>
                    {!isContentBlockContext() && (
                        <div>
                            <VerticalDivider />
                        </div>
                    )}
                    <div className="slds-col">
                        <VimeoPlayer video={video} />
                    </div>
                    {error && (
                        <ToastContainer>
                            <Toast
                                labels={{
                                    heading: error,
                                }}
                                variant="error"
                                onRequestClose={() => setError("")}
                            />
                        </ToastContainer>
                    )}
                </div>
            )}
        </>
    );
}
